import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { CreditCard, Sparkles, ChevronRight } from "lucide-react";
import { fetchPlan } from "../../store/actions/billingActions";

const Wrapper = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-radius: 12px;
  border: 1px solid
    ${({ $trial, theme }) =>
      $trial ? "rgba(155, 135, 245, 0.45)" : (theme.colors?.border || "rgba(155, 135, 245, 0.3)")};
  background: ${({ $trial }) =>
    $trial
      ? "linear-gradient(135deg, rgba(155, 135, 245, 0.18) 0%, rgba(126, 105, 171, 0.1) 100%)"
      : "rgba(155, 135, 245, 0.06)"};
  color: ${({ theme }) => theme.colors?.text?.body || "#374151"};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, box-shadow 0.2s;

  &:hover {
    background: ${({ $trial }) =>
      $trial ? "rgba(155, 135, 245, 0.22)" : "rgba(155, 135, 245, 0.12)"};
    border-color: rgba(155, 135, 245, 0.6);
    box-shadow: 0 2px 8px rgba(155, 135, 245, 0.15);
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ $trial }) =>
    $trial ? "rgba(155, 135, 245, 0.25)" : "rgba(155, 135, 245, 0.12)"};
  color: ${({ theme }) => theme.colors?.primary?.DEFAULT || "#7c3aed"};
`;

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  text-align: left;
`;

const Label = styled.span`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors?.text?.muted || "#6b7280"};
  line-height: 1.2;
`;

const PlanName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors?.primary?.DEFAULT || "#7c3aed"};
  line-height: 1.3;
`;

const TrialMeta = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors?.text?.muted || "#6b7280"};
  margin-top: 1px;
`;

const Chevron = styled(ChevronRight)`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors?.text?.muted || "#9ca3af"};
`;

function getDaysRemaining(endDate) {
  if (!endDate) return null;
  const end = new Date(endDate);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
}

export function PlanBadge() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const billing = useSelector((state) => state.billing) || {};
  const { planName, plan, trialEndsAt, isTrialing } = billing;

  useEffect(() => {
    dispatch(fetchPlan());
  }, [dispatch]);

  const isTrial = plan === "free_trial" || isTrialing;
  const displayName = planName || (isTrial ? "Free Trial" : "Free");
  const trialEndDate = trialEndsAt ? new Date(trialEndsAt) : null;
  const trialEndFormatted = trialEndDate?.toLocaleDateString(undefined, { dateStyle: "medium" });
  const daysLeft = getDaysRemaining(trialEndsAt);

  const handleClick = () => navigate("/billing");

  return (
    <Wrapper type="button" onClick={handleClick} $trial={isTrial} aria-label="Current plan – open billing">
      <Left>
        <IconWrap $trial={isTrial}>
          {isTrial ? <Sparkles size={18} /> : <CreditCard size={18} />}
        </IconWrap>
        <TextBlock>
          <Label>{isTrial ? "Free trial" : "Plan"}</Label>
          <PlanName>{displayName}</PlanName>
          {isTrial && (daysLeft !== null || trialEndFormatted) && (
            <TrialMeta>
              {daysLeft !== null && daysLeft >= 0
                ? `${daysLeft} day${daysLeft !== 1 ? "s" : ""} left`
                : `Ends ${trialEndFormatted}`}
            </TrialMeta>
          )}
        </TextBlock>
      </Left>
      <Chevron size={18} />
    </Wrapper>
  );
}
